/**
 * Factory BOM Cost API — dedicated cho `/factories/{factoryCode}/bom-cost`.
 *
 * Mirror pattern factoryBomApi: list / options / detail / create / update / delete.
 * Master cross-factory (bảng `cost_item` không có column factory_code) — `factoryCode`
 * chỉ dùng auth scope.
 */
import { baseApi } from './baseApi'

export type CostCell = string | number | boolean | null

export interface CostListRow {
  cost_item_id: number
  cost_item_code: string
  display_code: string | null
  cost_group_code: string
  cost_group_name_vi: string | null
  cost_group_name_en: string | null
  cost_item_name_vi: string
  cost_item_name_en: string | null
  unit_price: number | null
  currency: string | null
  price_unit: string | null
  valid_from: string | null
  valid_to: string | null
  vendor_name: string | null
  vendor_contract_no: string | null
  vendor_note: string | null
  vendor_log: string | null
  calc_method: string | null
  description: string | null
  cost_group_sort_order: number | null
  cost_item_sort_order: number | null
  status_name: string | null
  updated_date: string | null
  updated_by: string | null
  [extra: string]: CostCell | undefined
}

export interface CostListResponse {
  items: CostListRow[]
  total: number
  source: string
}

export type CostDetail = CostListRow

export interface CostGroupOption {
  cost_group_code: string
  cost_group_name_vi: string | null
  cost_group_name_en: string | null
  sort_order: number | null
}

export interface CostOptions {
  costGroups: CostGroupOption[]
  calcMethods: string[]
}

export interface CostSavePayload {
  cost_item_code: string
  display_code?: string | null
  cost_group_code: string
  cost_item_name_vi: string
  cost_item_name_en?: string | null
  unit_price?: number | null
  currency?: string | null
  price_unit?: string | null
  valid_from?: string | null
  valid_to?: string | null
  vendor_name?: string | null
  vendor_contract_no?: string | null
  vendor_note?: string | null
  calc_method: string
  sort_order?: number | null
  description?: string | null
}

const costUrl = (factoryCode: string, ...segments: string[]): string =>
  ['/factories', factoryCode, 'bom-cost', ...segments].filter(Boolean).join('/')

export const factoryCostApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFactoryCostList: builder.query<
      CostListResponse,
      { factoryCode: string; costGroupCode?: string }
    >({
      query: ({ factoryCode, costGroupCode }) => ({
        url: costUrl(factoryCode),
        params: costGroupCode ? { cost_group_code: costGroupCode } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.costGroupCode ?? 'all'}` },
      ],
    }),

    getFactoryCostOptions: builder.query<CostOptions, string>({
      query: (factoryCode) => costUrl(factoryCode, 'options'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'FactoryCost', id: `${factoryCode}:options` },
      ],
    }),

    getFactoryCostDetail: builder.query<
      CostDetail,
      { factoryCode: string; costItemId: number }
    >({
      query: ({ factoryCode, costItemId }) => ({
        url: costUrl(factoryCode, String(costItemId)),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:${arg.costItemId}` },
      ],
    }),

    createFactoryCost: builder.mutation<
      CostDetail,
      { factoryCode: string; body: CostSavePayload }
    >({
      query: ({ factoryCode, body }) => ({
        url: costUrl(factoryCode),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.body.cost_group_code}` },
      ],
    }),

    updateFactoryCost: builder.mutation<
      CostDetail,
      { factoryCode: string; costItemId: number; body: CostSavePayload }
    >({
      query: ({ factoryCode, costItemId, body }) => ({
        url: costUrl(factoryCode, String(costItemId)),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.body.cost_group_code}` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:${arg.costItemId}` },
      ],
    }),

    deleteFactoryCost: builder.mutation<
      void,
      { factoryCode: string; costItemId: number; costGroupCode?: string }
    >({
      query: ({ factoryCode, costItemId }) => ({
        url: costUrl(factoryCode, String(costItemId)),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:list:${arg.costGroupCode ?? 'all'}` },
        { type: 'FactoryCost', id: `${arg.factoryCode}:${arg.costItemId}` },
      ],
    }),
  }),
})

export const {
  useGetFactoryCostListQuery,
  useGetFactoryCostOptionsQuery,
  useGetFactoryCostDetailQuery,
  useCreateFactoryCostMutation,
  useUpdateFactoryCostMutation,
  useDeleteFactoryCostMutation,
} = factoryCostApi
